import {
  ChevronLeft,
  ChevronRight,
  FilterAltOutlined,
  IosShare,
  OpenInFullOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { Skeleton } from 'antd'
import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { DropdownMenu } from 'sutra/components'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import FilterDisplay from 'components/chart-v2/components/FilterDisplay'
import MultiDomainChart from 'components/chart-v2/components/MultiDomainChart'
import type { ChartFilter } from 'components/chart-v2/model'
import { DataPoint } from 'components/chart-v2/model'
import YogiDivider from 'components/ui/YogiDivider'
import {
  AssetCaption,
  IconButton,
} from 'features/ask-yogi/features/conversation/components/message-content/message-content-styles'
import { useChartsLinkKey } from 'features/ask-yogi/features/conversation/hooks/use-charts-link-key'
import {
  isYogiQLChatRequest,
  useYogiQLChatChart,
} from 'features/ask-yogi/features/conversation/hooks/use-yogiql-chat-chart'
import { captureAskYogiChartCanvas } from 'features/ask-yogi/features/conversation/utils/capture-chart-canvas'
import type { AskYogiChartExportHandle } from 'features/ask-yogi/features/conversation/utils/capture-chart-slides'
import { storedLinkPanel } from 'features/ask-yogi/features/conversation/utils/charts-link-state'
import { groupedRowsOrLeaves } from 'features/ask-yogi/features/conversation/utils/grouped-rows'
import { useIsDashboards21Enabled } from 'features/ask-yogi/hooks/use-ask-yogi-feature-flag'
import { useAskYogiService } from 'features/ask-yogi/service'
import { askYogiTokens } from 'features/ask-yogi/styles/tokens'
import type {
  ArtifactChartPage,
  AskYogiChartResponse,
} from 'features/ask-yogi/types'
import type { ChartExportColumn } from 'features/charts-v2/build-chart-export-table'
import { chartFactory } from 'features/charts-v2/chart-factory'
import { ChartLegend } from 'features/charts-v2/components/ChartLegend'
import { deriveCustomBreakdowns } from 'features/charts-v2/derive-custom-breakdowns'
import { applyLineHoverEmphasis } from 'features/charts-v2/line-hover-emphasis'
import { CHARTS_2_URL } from 'features/charts-v2/paths'
import { resolveExportBreakdownColumns } from 'features/charts-v2/resolve-export-breakdown-columns'
import {
  countRidgelineRidges,
  dropRidgelineBinDomain,
  resolveRidgelineMetrics,
} from 'features/charts-v2/ridgeline-artifact'
import {
  resolveSankeyBreakdowns,
  resolveSankeyMetrics,
} from 'features/charts-v2/sankey-artifact'
import { useChartQuery } from 'features/charts-v2/service'
import { useChartsV2StateService } from 'features/charts-v2/service/state-service'
import type { ChartValue } from 'features/charts-v2/types'
import type { ChartProps } from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import {
  isYogiQLStoredPanel,
  type StoredPanel,
} from 'features/charts-v2/types/stored-panel'
import type { ChartRequest } from 'features/charts-v2/types/yogiql'
import {
  buildChartsV2State,
  CHART_TYPE_MAP,
  chartArtifactToPanels,
  conformantArtifactToPanels,
  getBreakdownFromDomain,
  getMetrics,
  getTimeBucket,
  type ResolvedFilter,
  TIME_BUCKET_MAP,
} from 'features/charts-v2/utils/chart-artifact-to-panels'
import { exportChartAsCSV } from 'features/charts-v2/utils/export-chart-as-csv'
import { getMetricLabel } from 'features/charts-v2/utils/metric-format-utils'
import { onCopyChart } from 'features/charts/utils'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { useProjectStore } from 'features/project/store'
import { buildProjectPageUrl } from 'routes/builders'
import type { HashNavigationState } from 'routes/navigation-state'
import {
  type ChartRenderStatus,
  trackChartRendered,
} from 'trackers/chart-events'
import mixpanel from 'trackers/mixpanel'
import { ProjectState, type SearchTermsLogical } from 'types/project'
import { buildSvgOnClone } from 'utils/svg-export-utils'

type LegacyProps = {
  data: DataPoint[]
  identifier: Record<string, unknown>
  filters?: ChartFilter[]
  request?: never
}

type ArtifactProps = {
  request: Record<string, unknown>
  title?: string
  /**
   * The page a charts-page turn left. "View in charts" opens every one of its
   * panels, in place of the one panel `request` draws.
   */
  page?: ArtifactChartPage
  /** The panel of `page` on screen, which "View in charts" makes active. */
  pageIndex?: number
  data?: never
  identifier?: never
  filters?: never
}

type Props = (LegacyProps | ArtifactProps) & {
  conversationUuid: string | undefined
  messageUuid: string
  // Identifies this chart within its message for the "View in charts"
  // handoff and the collapsed-chip match on return — `artifact.result_id`
  // for the new-artifact format, the stringified part index for legacy.
  chartKey: string
}

const ROW_HEIGHT_PX = 36
const MIN_CHART_HEIGHT_PX = 300

function getChartHeight(rowCount: number): number {
  return Math.max(MIN_CHART_HEIGHT_PX, rowCount * ROW_HEIGHT_PX)
}

function getPieChartContainerHeightPx(): number {
  return 320
}

function getFixedPlaneChartContainerHeightPx(): number {
  return 480
}

function getLineChartContainerHeightPx(): number {
  return 400
}

function getTableChartContainerHeightPx(rowCount: number): number {
  // SummaryMetrics reserves ~220px for header + summary footer + pagination
  // + padding; remaining height is the scrollable body at ~40px per data row.
  // Size the container so 6 rows fit without scroll, scroll past the cap.
  const FIXED_OVERHEAD_PX = 220
  const TABLE_ROW_HEIGHT_PX = 40
  const natural = FIXED_OVERHEAD_PX + rowCount * TABLE_ROW_HEIGHT_PX
  return Math.max(MIN_CHART_HEIGHT_PX, Math.min(600, natural))
}

// Expand modal: every chart type renders at a larger natural size with no
// inline truncation, so the user sees the full dataset and can export that
// full view. Width of the modal itself is `EXPAND_MODAL_WIDTH_PX` below.
const EXPAND_MODAL_WIDTH_PX = 960

function getExpandedPieChartContainerHeightPx(): number {
  return 500
}

function getExpandedFixedPlaneChartContainerHeightPx(): number {
  return 600
}

function getExpandedLineChartContainerHeightPx(): number {
  return 550
}

function getExpandedTableChartContainerHeightPx(rowCount: number): number {
  // Size for one page — SummaryMetrics paginates at 100 rows by default,
  // so sizing past that leaves whitespace below the rendered page.
  const FIXED_OVERHEAD_PX = 170
  const TABLE_ROW_HEIGHT_PX = 36
  const DEFAULT_PAGE_SIZE = 100
  const visibleRowCount = Math.min(rowCount, DEFAULT_PAGE_SIZE)
  return Math.max(
    MIN_CHART_HEIGHT_PX,
    FIXED_OVERHEAD_PX + visibleRowCount * TABLE_ROW_HEIGHT_PX,
  )
}

// SVG-based charts (heatmap, treemap, scatter, bubble, line) need an extra
// pre-rasterization step or html2canvas produces a blank PNG. Charts that need
// it carry a [data-chart-export-root="true"] marker; `buildSvgOnClone` returns
// a noop (null) for charts that don't, so this is safe to call unconditionally.
async function copyChartWithSvgSupport(
  contentRef: React.RefObject<HTMLDivElement>,
  areaRef: React.RefObject<HTMLDivElement>,
  title: string | undefined,
  isPrint = false,
): Promise<true | undefined> {
  if (!contentRef.current) return undefined
  const onclone = await buildSvgOnClone(contentRef.current)
  return onCopyChart(
    contentRef,
    areaRef,
    title,
    isPrint,
    undefined,
    onclone ? { onclone } : undefined,
  )
}

// Dedup Chart Rendered to one emit per unique chart, not per remount.
// Keyed by (messageUuid, request) so multi-chart messages emit once per chart
// — `request` is the per-chart artifact identifier (same shape react-query
// uses to key its cache below).
const RENDERED_CHARTS_STORAGE_KEY = 'ay:chart-rendered-chart-keys'
const RENDERED_CHARTS_CAP = 1500

function getChartTrackingKey(
  messageUuid: string,
  request: Record<string, unknown>,
): string {
  return `${messageUuid}:${JSON.stringify(request)}`
}

function hasChartBeenTracked(chartKey: string): boolean {
  try {
    const raw = localStorage.getItem(RENDERED_CHARTS_STORAGE_KEY)
    if (!raw) return false
    const keys = JSON.parse(raw) as string[]
    return keys.includes(chartKey)
  } catch {
    return false
  }
}

function markChartTracked(chartKey: string): void {
  try {
    const raw = localStorage.getItem(RENDERED_CHARTS_STORAGE_KEY)
    const keys = raw ? (JSON.parse(raw) as string[]) : []
    if (keys.includes(chartKey)) return
    keys.push(chartKey)
    const trimmed = keys.slice(-RENDERED_CHARTS_CAP)
    localStorage.setItem(RENDERED_CHARTS_STORAGE_KEY, JSON.stringify(trimmed))
  } catch {
    // localStorage unavailable (incognito / disabled) — fall through.
  }
}

function trackAskYogiChartAction(
  action:
    | 'copy_chart_png'
    | 'download_chart_png'
    | 'download_chart_csv'
    | 'expand_chart'
    | 'open_chart_filters_modal'
    | 'chart_metric_toggle'
    | 'chart_sort_toggle'
    | 'chart_legend_hide'
    | 'view_in_charts',
  payload: {
    conversationUuid: string | undefined
    messageUuid: string
    chartType: string
  },
): void {
  mixpanel.track('askyogi', {
    action,
    chart_format: 'artifact',
    chart_type: CHART_TYPE_MAP[payload.chartType] ?? payload.chartType,
    conversation_uuid: payload.conversationUuid,
    message_uuid: payload.messageUuid,
  })
}

const ArtifactChart = React.memo(
  React.forwardRef<
    AskYogiChartExportHandle,
    {
      request: Record<string, unknown>
      messageUuid: string
      conversationUuid: string | undefined
      title?: string
      chartKey: string
      page?: ArtifactChartPage
      pageIndex?: number
    }
  >((props, ref) => {
    const {
      request,
      messageUuid,
      conversationUuid,
      title,
      chartKey,
      page,
      pageIndex,
    } = props
    const askYogiService = useAskYogiService()
    const navigate = useNavigate()
    const isDashboards21Enabled = useIsDashboards21Enabled()
    // bully ignore[no-service-hooks-outside-containers: ArtifactChart already owns its own data orchestration (useAskYogiService/useQuery/useProjectStore on adjacent lines) — a Container/View split of this 800-line component is out of scope; refactor: when ArtifactChart is split into Container + View, view-service-hooks: same site, wired alongside the existing useAskYogiService; refactor: when ArtifactChart is split into Container + View]
    const chartsV2StateService = useChartsV2StateService()
    const projectId = useProjectStore((state: ProjectState) => state.projectId)
    const mintChartsLinkKey = useChartsLinkKey(projectId, askYogiService)
    const [activeMetricIndex, setActiveMetricIndex] = useState(0)
    const [filterModalOpen, setFilterModalOpen] = useState(false)
    const [expandModalOpen, setExpandModalOpen] = useState(false)
    const [sortAscending, setSortAscending] = useState(false)
    const [exportMenuOpen, setExportMenuOpen] = useState(false)
    const [expandExportMenuOpen, setExpandExportMenuOpen] = useState(false)
    const [hoveredLegendCategory, setHoveredLegendCategory] = useState<
      string | null
    >(null)
    const [legendOpen, setLegendOpen] = useState(true)
    const chartAreaRef = useRef<HTMLDivElement>(null)
    const chartContentRef = useRef<HTMLDivElement>(null)
    const expandedChartAreaRef = useRef<HTMLDivElement>(null)
    const expandedChartContentRef = useRef<HTMLDivElement>(null)

    // Exposed for the "Export entire convo → PDF" batch capture
    // (`capture-chart-slides.ts`) — reads `chartContentRef` at call time, so it
    // naturally resolves to `undefined` while this chart is still on its
    // Skeleton/error early return below (nothing attaches the ref there yet),
    // the same race the single-chart "Download PNG" button already has.
    useImperativeHandle(
      ref,
      () => ({
        captureChart: async () => {
          const canvas = await captureAskYogiChartCanvas(
            chartContentRef.current,
          )
          if (!canvas) return undefined
          return {
            dataUrl: canvas.toDataURL('image/png'),
            aspectRatio: canvas.width / canvas.height,
          }
        },
      }),
      [],
    )

    const defaultFilterList = useFilterUIStore(
      (state: FilterUIState) => state.defaultFilterList,
    )
    const customBreakdowns = useMemo(
      () => deriveCustomBreakdowns(defaultFilterList || []),
      [defaultFilterList],
    )
    // A YogiQL chat chart re-runs on the YogiQL route on every hub, never
    // through the chat's chart route; everything else draws as before.
    const yogiql = useYogiQLChatChart(projectId, request, page?.panels)
    const isYogiQL = yogiql.chart.status !== 'legacy'
    const ready = yogiql.chart.status === 'ready' ? yogiql.chart : null
    const legacy = useQuery(
      ['ask-yogi-chart', projectId, messageUuid, request],
      () => askYogiService.fetchChartByRequest(projectId, request),
      { enabled: !!projectId && !isYogiQL, refetchOnWindowFocus: false },
    )
    const yogiqlQuery = useChartQuery(projectId, ready?.panel ?? null, {
      enabled: !!ready,
      yogiql: isYogiQL,
      forceYogiQL: isYogiQL,
      yogiqlNames: ready?.names,
    })
    const unavailable =
      yogiql.chart.status === 'unavailable' || yogiqlQuery.yogiqlUnavailable
    const chartResponse: AskYogiChartResponse | undefined = isYogiQL
      ? (yogiqlQuery.data ?? undefined)
      : legacy.data
    const isLoading = isYogiQL
      ? yogiql.chart.status === 'pending' || yogiqlQuery.isLoading
      : legacy.isLoading
    const chartError = isYogiQL ? yogiqlQuery.error : legacy.error

    useEffect(() => {
      if (isLoading) return
      const chartKey = getChartTrackingKey(messageUuid, request)
      if (hasChartBeenTracked(chartKey)) return
      let status: ChartRenderStatus
      let error_code: string | undefined
      if (chartError) {
        status = 'backend_error'
        error_code = (chartError as Error)?.message
      } else if (!chartResponse) {
        return
        // The rows the chart DREW, not the leaf rows. A grouped request whose
        // rollup came back empty renders blank while `data` is non-empty, so
        // testing `data` reported `success` for the one case
        // `groupedRowsOrLeaves` deliberately renders empty — a healthy chart
        // in the metrics and nothing on screen.
      } else if (groupedRowsOrLeaves(chartResponse).length === 0) {
        status = 'empty'
      } else {
        status = 'success'
      }
      markChartTracked(chartKey)
      const rawType = (request['chart_type'] as string | undefined) ?? 'bar'
      trackChartRendered({
        surface: 'ask_yogi',
        chart_type: CHART_TYPE_MAP[rawType] ?? rawType,
        status,
        error_code,
        conversation_uuid: conversationUuid,
        message_uuid: messageUuid,
        is_initial_render: true,
      })
    }, [
      isLoading,
      chartError,
      chartResponse,
      messageUuid,
      conversationUuid,
      request,
    ])

    useEffect(() => {
      const apply = (root: HTMLElement | null): void => {
        if (!root) return
        applyLineHoverEmphasis(root, hoveredLegendCategory)
      }
      apply(chartContentRef.current)
      apply(expandedChartContentRef.current)
    }, [hoveredLegendCategory])

    if (unavailable) {
      return (
        <ValidationError>{"This chart can't be shown here"}</ValidationError>
      )
    }
    if (isLoading || !chartResponse) {
      return <Skeleton active paragraph={{ rows: 6 }} />
    }

    const { identifier, filters } = chartResponse
    const chartTypeKey = (request['chart_type'] as string | undefined) ?? 'bar'
    const chartType = CHART_TYPE_MAP[chartTypeKey] ?? ChartTypeV2.BAR_HORIZONTAL
    const isSankeyChart = chartType === ChartTypeV2.SANKEY
    // A Sankey draws a grouped axis as group -> member columns, built from the
    // leaf rows and the category mapping, so it must not take the rolled-up rows.
    const rows = isSankeyChart
      ? (chartResponse.data ?? [])
      : groupedRowsOrLeaves(chartResponse)

    const strategy = chartFactory.getStrategy(chartType)
    if (!strategy) return <Container />

    const domains = (identifier.domains ?? []) as Array<{ fn?: string }>
    const aggregatedValues = (identifier.aggregated_values ?? []) as Array<{
      fn: string
      args: unknown[]
    }>

    const isRidgelineChart = chartType === ChartTypeV2.RIDGELINE
    // A conformant artifact carries the PANEL it drew, and the panel is what
    // the charts page itself renders from. Everything below reads it first and
    // reverse-maps the identifier only for a legacy artifact, which has no
    // panel. The reverse map is lossy: a Source Type domain is a nested
    // coalesce/case with no bare dimension at `args[0]`, and a custom field is
    // `json_unnest` over `custom`, so both came back null and a table grouped
    // by them collapsed to one "Total" row (PROD-6100).
    // A YogiQL chart's identifier is empty: its panel is the decoded request.
    const requestPanel =
      ready?.panel ?? (request['panel'] as ChartPanel | undefined)
    const wireMetrics = ready
      ? [...ready.panel.metrics]
      : getMetrics(aggregatedValues)
    const timeBucket = requestPanel
      ? requestPanel.timeBucket
      : getTimeBucket(domains)
    // A ridgeline draws ONE score metric from (breakdown, bin) cell rows, so
    // the wire's [count, avg] metrics must not become metric tabs or slices.
    // A Sankey sizes its flows by the count alone, so the wire's trailing
    // [count, avg] sentiment must not become a metric tab or slice either.
    const allMetrics = isRidgelineChart
      ? resolveRidgelineMetrics(requestPanel?.metrics, wireMetrics)
      : isSankeyChart
        ? resolveSankeyMetrics(requestPanel?.metrics, wireMetrics)
        : wireMetrics
    // Order matches backend domain order — StackedBar reads breakdowns[0]
    // as primary (y-axis) and breakdowns[1] as the stacking segment.
    const domainBreakdowns = (
      isRidgelineChart ? dropRidgelineBinDomain(domains) : domains
    )
      .filter((d) => !d.fn || !(d.fn in TIME_BUCKET_MAP))
      .map((d) => getBreakdownFromDomain(d, customBreakdowns))
      .filter((b): b is NonNullable<typeof b> => b !== null)
    const breakdowns = isSankeyChart
      ? resolveSankeyBreakdowns(requestPanel?.breakdowns, domainBreakdowns)
      : requestPanel
        ? [...requestPanel.breakdowns]
        : domainBreakdowns
    const csvBreakdownOptions: ChartExportColumn[] =
      resolveExportBreakdownColumns(breakdowns)
    const isMultiMetric = allMetrics.length > 1

    // Panels for the "View in charts" deep-link. Empty ⇒ nothing maps ⇒ hide
    // the button. Uses the stricter no-record_count-fallback builder, so this is
    // NOT the same as `allMetrics` above (which falls back to record_count).
    // A conformant artifact carries the PANEL it drew, so the deep-link uses it
    // verbatim rather than rebuilding one. The legacy builder reads
    // `resolved_filters` / `date_range`, which the conformant chart response
    // does not set — so it opened an UNFILTERED chart, with no error.
    const conformantPanel = requestPanel
    const viewInChartsPanels = conformantPanel
      ? conformantArtifactToPanels(conformantPanel, title)
      : chartArtifactToPanels({
          chartTypeKey,
          domains: domains as Array<{ fn?: string; args?: unknown[] }>,
          aggregatedValues,
          resolvedFilters: chartResponse.resolved_filters as
            | ResolvedFilter[]
            | undefined,
          dateRange: chartResponse.date_range,
          searchTermsLogical: request['search_terms'] as
            | SearchTermsLogical
            | undefined,
          customBreakdowns,
          filterOptions: defaultFilterList || undefined,
          title,
        })
    // A page panel that does not decode is null: skipped, not the default.
    const pageEntries = yogiql.pagePanels?.map(
      (p) =>
        p && conformantArtifactToPanels(p.panel as ChartPanel, p.title)[0]!,
    )
    const activePagePanelId = pageEntries?.[pageIndex ?? 0]?.id
    const pagePanels = pageEntries
      ?.filter((p): p is ChartPanel => !!p)
      .map((p, order) => ({ ...p, order }))
    const canViewInCharts = (pagePanels ?? viewInChartsPanels).length > 0

    const safeIndex = Math.min(activeMetricIndex, allMetrics.length - 1)
    const activeMetric = allMetrics[safeIndex]!

    const isLineChart = chartType === ChartTypeV2.LINE
    const isPieChart = chartType === ChartTypeV2.PIE
    const isStackedBarChart = chartType === ChartTypeV2.BAR_HORIZONTAL_STACKED
    const isBubbleChart = chartType === ChartTypeV2.BUBBLE
    const isScatterChart = chartType === ChartTypeV2.SCATTER
    const isAxisChart = isBubbleChart || isScatterChart
    const isHeatmapChart = chartType === ChartTypeV2.HEATMAP
    const isTreemapChart = chartType === ChartTypeV2.TREEMAP
    const isFixedPlaneChart =
      isAxisChart || isHeatmapChart || isTreemapChart || isSankeyChart
    const isTableChart = chartType === ChartTypeV2.TABLE
    // Axis/heatmap/treemap/table render every metric at once; bar/pie
    // toggle to one metric at a time via the metric tabs. Line renders
    // multi-metric as dual-axis (scored on left Y, volume on right Y) —
    // the strategy needs both metrics passed through, not pre-sliced.
    const rendersAllMetrics = isFixedPlaneChart || isTableChart || isLineChart

    // "View in charts" deep-link panels. For toggleable charts the link should
    // open only the metric the user is currently looking at (one panel), not
    // every aggregation. Match by metric rather than index: the toggle tabs use
    // `allMetrics` (looser getMetrics, with record_count fallback) while the
    // panels come from the stricter chartArtifactToPanels, so the two arrays
    // aren't guaranteed index-aligned. No match (e.g. record_count) ⇒ fall back
    // to the full set. All-metric charts already produce one combined panel.
    // A page's chart opens the whole page, whichever metric is showing.
    const linkPanels =
      pagePanels ??
      (rendersAllMetrics || !isMultiMetric
        ? viewInChartsPanels
        : (() => {
            const match = viewInChartsPanels.find(
              (p) => p.metrics[0] === activeMetric,
            )
            return match ? [match] : viewInChartsPanels
          })())

    // The same panels as the link stores them: each YogiQL one as the request
    // the chat drew, so the page reads that request, not a decoded panel.
    const storedLinkPanels: StoredPanel[] = pageEntries
      ? pageEntries
          .map((p, i) => {
            const stored = page?.panels[i]?.panel
            return p && isYogiQLStoredPanel(stored)
              ? storedLinkPanel(p, stored.request)
              : p
          })
          .filter((p): p is StoredPanel => !!p)
          .map((p, order) => ({ ...p, order }))
      : ready && isYogiQLChatRequest(request)
        ? linkPanels.map((p) =>
            storedLinkPanel(p, request['request'] as ChartRequest),
          )
        : linkPanels

    // Slice to the active metric column for single-metric charts. All-metric
    // charts (axis/heatmap/treemap/table) need every values[] entry — bubble
    // reads values[0]=x, [1]=y, [2]=size; heatmap reads [0]=color, [1]=volume.
    const activeData =
      isMultiMetric && !rendersAllMetrics
        ? rows.map((row: { category: unknown[]; values: unknown[] }) => ({
            ...row,
            values: [row.values[safeIndex]],
          }))
        : rows

    // Stacked bar emits one row per (primary, segment); count unique primaries
    // so chart height matches rendered rows, not segment×primary. A ridgeline's
    // rows are (breakdown, bin) cells, counted as ridges.
    const heightRowCount = isRidgelineChart
      ? countRidgelineRidges(activeData as Array<{ category?: unknown[] }>)
      : isStackedBarChart
        ? new Set(
            (activeData as Array<{ category?: unknown[] }>).map((r) =>
              JSON.stringify(r.category?.[0] ?? null),
            ),
          ).size
        : activeData.length

    const chartHeight = isPieChart
      ? getPieChartContainerHeightPx()
      : isFixedPlaneChart
        ? getFixedPlaneChartContainerHeightPx()
        : isLineChart
          ? getLineChartContainerHeightPx()
          : isTableChart
            ? getTableChartContainerHeightPx(heightRowCount)
            : getChartHeight(heightRowCount)

    // Expand modal: every chart renders at its uncapped natural size so the
    // user sees the full dataset and can export that "full" view. Bar/stacked
    // render uncapped so every bar is at full thickness; fixed-plane / pie /
    // line / table render at a larger fixed-or-natural height than inline.
    const expandedChartHeight = isPieChart
      ? getExpandedPieChartContainerHeightPx()
      : isFixedPlaneChart
        ? getExpandedFixedPlaneChartContainerHeightPx()
        : isLineChart
          ? getExpandedLineChartContainerHeightPx()
          : isTableChart
            ? getExpandedTableChartContainerHeightPx(heightRowCount)
            : getChartHeight(heightRowCount)

    const chartSlice = {
      identifier,
      data: activeData as ChartValue[],
      ...(isSankeyChart && {
        category_mapping: chartResponse.category_mapping,
      }),
    }

    const chartProps: ChartProps = {
      data: chartSlice as ChartProps['data'],
      selectedMetrics: rendersAllMetrics ? allMetrics : [activeMetric],
      selectedBreakdowns: breakdowns.length > 0 ? breakdowns : undefined,
      timeBucket,
      yAxisOverflow: 'truncate',
      sortDirection: sortAscending ? 'ASC' : 'DESC',
      // Hovering a datapoint dot drives the same series emphasis as hovering
      // the legend (applyLineHoverEmphasis reads this state for both roots).
      onDataPointHover: setHoveredLegendCategory,
      // Deliberate, not inherited from the legacy prop default: conversation
      // charts have no display-settings surface, so labels are always 'on'
      // (occlusion-culled on multi-series charts).
      datapointLabelsMode: 'on',
      // The Summary Metrics table computes its % Change columns by shifting
      // this panel's date range and re-querying; without it the columns are
      // omitted, which is right for a legacy artifact and wrong for a page
      // chip, whose page shows them. Other renderers ignore it.
      panel: requestPanel,
    }

    const exportMetrics = rendersAllMetrics ? allMetrics : [activeMetric]

    const handleLegendToggle = (): void => {
      if (legendOpen) {
        trackAskYogiChartAction('chart_legend_hide', {
          conversationUuid,
          messageUuid,
          chartType: chartTypeKey,
        })
      }
      setLegendOpen((v) => !v)
    }

    const expandedChartProps: ChartProps = {
      ...chartProps,
      yAxisOverflow: 'truncate',
    }

    const transformedData = strategy.transform(chartProps)
    const expandedTransformedData = strategy.transform(expandedChartProps)
    const validation = strategy.validate(chartProps)

    if (!validation.isValid) {
      return (
        <ValidationError>
          {validation.errorTitle}
          {validation.errorDescription
            ? ` — ${validation.errorDescription}`
            : ''}
        </ValidationError>
      )
    }

    const hasFilters = filters && filters.length > 0

    return (
      <ChartWrapper ref={chartAreaRef}>
        <ChartHeader>
          {title && <ChartTitleSlot>{title}</ChartTitleSlot>}
          <RightButtons>
            {!isLineChart && !isPieChart && !rendersAllMetrics && (
              <Tooltip
                content={sortAscending ? 'Sort ascending' : 'Sort descending'}
              >
                <IconButton
                  onClick={() => {
                    trackAskYogiChartAction('chart_sort_toggle', {
                      conversationUuid,
                      messageUuid,
                      chartType: chartTypeKey,
                    })
                    setSortAscending((prev) => !prev)
                  }}
                >
                  {sortAscending ? (
                    <ArrowUpwardIcon style={{ fontSize: 14 }} />
                  ) : (
                    <ArrowDownwardIcon style={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {hasFilters && (
              <Tooltip content="Filters">
                <IconButton
                  onClick={() => {
                    trackAskYogiChartAction('open_chart_filters_modal', {
                      conversationUuid,
                      messageUuid,
                      chartType: chartTypeKey,
                    })
                    setFilterModalOpen(true)
                  }}
                >
                  <FilterAltOutlined style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip content="Expand chart">
              <IconButton
                onClick={() => {
                  trackAskYogiChartAction('expand_chart', {
                    conversationUuid,
                    messageUuid,
                    chartType: chartTypeKey,
                  })
                  setExpandModalOpen(true)
                }}
              >
                <OpenInFullOutlined style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <ToolbarDivider />
            <Tooltip content="Export">
              <DropdownTrigger>
                <DropdownMenu
                  open={exportMenuOpen}
                  onOpenChange={setExportMenuOpen}
                  side="bottom"
                  align="end"
                  items={[
                    {
                      key: 'copy-png',
                      label: 'Copy PNG',
                      onClick: () => {
                        void copyChartWithSvgSupport(
                          chartContentRef,
                          chartAreaRef,
                          title,
                        )
                          .then((ok) => {
                            if (ok) {
                              trackAskYogiChartAction('copy_chart_png', {
                                conversationUuid,
                                messageUuid,
                                chartType: chartTypeKey,
                              })
                            }
                          })
                          .catch(() => {
                            toast.error('Failed to copy chart')
                          })
                      },
                    },
                    {
                      key: 'download-png',
                      label: 'Download PNG',
                      onClick: () => {
                        setTimeout(() => {
                          void copyChartWithSvgSupport(
                            chartContentRef,
                            chartAreaRef,
                            title,
                            true,
                          )
                            .then((ok) => {
                              if (ok) {
                                trackAskYogiChartAction('download_chart_png', {
                                  conversationUuid,
                                  messageUuid,
                                  chartType: chartTypeKey,
                                })
                              }
                            })
                            .catch(() => {
                              toast.error('Failed to download chart')
                            })
                        }, 50)
                      },
                    },
                    {
                      key: 'download-csv',
                      label: 'Download CSV',
                      onClick: () => {
                        void exportChartAsCSV(
                          chartSlice,
                          exportMetrics,
                          csvBreakdownOptions,
                          title ?? 'Chart',
                          null,
                          chartType,
                        )
                          .then((ok) => {
                            if (ok) {
                              trackAskYogiChartAction('download_chart_csv', {
                                conversationUuid,
                                messageUuid,
                                chartType: chartTypeKey,
                              })
                            }
                          })
                          .catch(() => {
                            toast.error('Failed to download chart CSV')
                          })
                      },
                    },
                  ]}
                >
                  <IconButton aria-label="Export">
                    <IosShare style={{ fontSize: 14 }} />
                  </IconButton>
                </DropdownMenu>
              </DropdownTrigger>
            </Tooltip>
            {canViewInCharts && (
              <Tooltip content="View in charts">
                <IconButton
                  data-chameleon-id="ay-view-in-charts"
                  onClick={() => {
                    trackAskYogiChartAction('view_in_charts', {
                      conversationUuid,
                      messageUuid,
                      chartType: chartTypeKey,
                    })
                    void mintChartsLinkKey(
                      storedLinkPanels,
                      activePagePanelId,
                      () =>
                        chartsV2StateService.save(projectId, {
                          page: CHARTS_2_URL,
                          filterString: JSON.stringify(
                            buildChartsV2State(linkPanels, activePagePanelId),
                          ),
                        }),
                    )
                      .then((hash) => {
                        const base = buildProjectPageUrl(
                          projectId,
                          CHARTS_2_URL,
                          hash,
                        )
                        if (isDashboards21Enabled) {
                          const params = new URLSearchParams({
                            source: 'ask_yogi',
                            message_uuid: messageUuid,
                            chart_key: chartKey,
                            ...(conversationUuid && {
                              cuid: conversationUuid,
                            }),
                          })
                          navigate(`${base}?${params.toString()}`, {
                            state: {
                              hashAuthority: 'url',
                            } satisfies HashNavigationState,
                          })
                        } else {
                          const params = new URLSearchParams({
                            source: 'ask_yogi',
                            message_uuid: messageUuid,
                            ...(conversationUuid && {
                              conversation_uuid: conversationUuid,
                            }),
                          })
                          window.open(`${base}?${params.toString()}`, '_blank')
                        }
                      })
                      .catch(() => {
                        toast.error('Could not open chart in charts page')
                      })
                  }}
                >
                  <OpenInNewOutlined style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </RightButtons>
        </ChartHeader>
        {isMultiMetric && !rendersAllMetrics && (
          <MetricToggle>
            {allMetrics.map((metric, i) => (
              <MetricToggleButton
                key={metric}
                $active={i === safeIndex}
                onClick={() => {
                  trackAskYogiChartAction('chart_metric_toggle', {
                    conversationUuid,
                    messageUuid,
                    chartType: chartTypeKey,
                  })
                  setActiveMetricIndex(i)
                }}
              >
                {getMetricLabel(metric)}
              </MetricToggleButton>
            ))}
          </MetricToggle>
        )}
        {isLineChart ? (
          <LineLegendRow ref={chartContentRef}>
            <ChartArea $height={chartHeight}>
              {strategy.render({ ...chartProps, transformedData })}
            </ChartArea>
            <LineLegendDrawer
              chartData={chartProps.data as never}
              selectedBreakdowns={breakdowns}
              onDataPointHover={setHoveredLegendCategory}
              open={legendOpen}
              onToggle={handleLegendToggle}
              height={chartHeight}
            />
          </LineLegendRow>
        ) : (
          <ChartArea ref={chartContentRef} $height={chartHeight}>
            {strategy.render({ ...chartProps, transformedData })}
          </ChartArea>
        )}
        <PopoverModal
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {title && <div style={{ color: '#28364f' }}>{title}</div>}
              <RightButtons>
                <Tooltip content="Export">
                  <DropdownTrigger>
                    <DropdownMenu
                      open={expandExportMenuOpen}
                      onOpenChange={setExpandExportMenuOpen}
                      side="bottom"
                      align="end"
                      items={[
                        {
                          key: 'copy-png',
                          label: 'Copy PNG',
                          onClick: () => {
                            void copyChartWithSvgSupport(
                              expandedChartContentRef,
                              expandedChartAreaRef,
                              title,
                            ).then((ok) => {
                              if (ok) {
                                trackAskYogiChartAction('copy_chart_png', {
                                  conversationUuid,
                                  messageUuid,
                                  chartType: chartTypeKey,
                                })
                              }
                            })
                          },
                        },
                        {
                          key: 'download-png',
                          label: 'Download PNG',
                          onClick: () => {
                            setTimeout(() => {
                              void copyChartWithSvgSupport(
                                expandedChartContentRef,
                                expandedChartAreaRef,
                                title,
                                true,
                              ).then((ok) => {
                                if (ok) {
                                  trackAskYogiChartAction(
                                    'download_chart_png',
                                    {
                                      conversationUuid,
                                      messageUuid,
                                      chartType: chartTypeKey,
                                    },
                                  )
                                }
                              })
                            }, 50)
                          },
                        },
                        {
                          key: 'download-csv',
                          label: 'Download CSV',
                          onClick: () => {
                            void exportChartAsCSV(
                              chartSlice,
                              exportMetrics,
                              csvBreakdownOptions,
                              title ?? 'Chart',
                              null,
                              chartType,
                            ).then((ok) => {
                              if (ok) {
                                trackAskYogiChartAction('download_chart_csv', {
                                  conversationUuid,
                                  messageUuid,
                                  chartType: chartTypeKey,
                                })
                              }
                            })
                          },
                        },
                      ]}
                    >
                      <IconButton aria-label="Export">
                        <IosShare style={{ fontSize: 14 }} />
                      </IconButton>
                    </DropdownMenu>
                  </DropdownTrigger>
                </Tooltip>
              </RightButtons>
            </div>
          }
          isOpen={expandModalOpen}
          onClose={() => setExpandModalOpen(false)}
          footer={null}
          width={EXPAND_MODAL_WIDTH_PX}
          content={
            <div ref={expandedChartAreaRef}>
              {isLineChart ? (
                <LineLegendRow ref={expandedChartContentRef}>
                  <ChartArea $height={expandedChartHeight}>
                    {strategy.render({
                      ...expandedChartProps,
                      transformedData: expandedTransformedData,
                    })}
                  </ChartArea>
                  <LineLegendDrawer
                    chartData={expandedChartProps.data as never}
                    selectedBreakdowns={breakdowns}
                    onDataPointHover={setHoveredLegendCategory}
                    open={legendOpen}
                    onToggle={handleLegendToggle}
                    height={expandedChartHeight}
                  />
                </LineLegendRow>
              ) : (
                <ChartArea
                  $height={expandedChartHeight}
                  ref={expandedChartContentRef}
                >
                  {strategy.render({
                    ...expandedChartProps,
                    transformedData: expandedTransformedData,
                  })}
                </ChartArea>
              )}
            </div>
          }
        />
        {hasFilters && (
          <PopoverModal
            title={
              <div style={{ color: '#28364f' }}>
                This chart was generated using these filters.
              </div>
            }
            isOpen={filterModalOpen}
            onClose={() => setFilterModalOpen(false)}
            footer={null}
            width={600}
            content={
              <>
                <YogiDivider
                  style={{
                    width: 'calc(100% + 40px)',
                    marginLeft: -20,
                    marginBottom: 16,
                  }}
                />
                <FilterDisplay filters={filters as never} />
              </>
            }
          />
        )}
      </ChartWrapper>
    )
  }),
)

ArtifactChart.displayName = 'ArtifactChart'

const Chart = React.memo(
  React.forwardRef<AskYogiChartExportHandle, Props>((props, ref) => {
    const { conversationUuid, messageUuid, request, chartKey } = props
    const askYogiService = useAskYogiService()

    if (request) {
      const { title, page, pageIndex } = props as ArtifactProps
      return (
        <ArtifactChart
          ref={ref}
          request={request}
          title={title}
          page={page}
          pageIndex={pageIndex}
          messageUuid={messageUuid}
          conversationUuid={conversationUuid}
          chartKey={chartKey}
        />
      )
    }

    const { data, identifier, filters } = props as LegacyProps

    const typedIdentifier = identifier as {
      aggregatedValues?: Array<{ fn: string; args: string[] }>
      constraints?: Array<{ fn: string; args: unknown[] }>
      domains?: Array<{ fn?: string; args?: unknown[] }>
    }

    return (
      <Container>
        {data && (
          <MultiDomainChart
            identifier={typedIdentifier as never}
            data={data}
            type={'bar'}
            aggregatedValues={typedIdentifier.aggregatedValues}
            conversationUuid={conversationUuid}
            messageUuid={messageUuid}
            filters={filters}
            fetchChart={(pid, id) =>
              askYogiService.fetchChart(pid, id as never)
            }
          />
        )}
      </Container>
    )
  }),
)

Chart.displayName = 'AskYogiChart'

export default Chart

const Container = styled.div`
  width: 100%;
  height: 400px;
`

const ChartWrapper = styled.div`
  width: 100%;
`

const ChartArea = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
  /* Recharts' ResponsiveContainer overshoots its parent by a sub-pixel or two;
     clip it here so it can't escape into an ancestor's overflow-y: auto and
     produce a spurious "barely scrollable" scrollbar. See ChartCanvas.tsx for
     the same fix on the dashboard surface. */
  overflow: hidden;
`

const LineLegendRow = styled.div`
  display: flex;
  width: 100%;
  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`

const LineLegendBox = styled.div<{ $open: boolean; $height: number }>`
  max-width: ${({ $open }) => ($open ? '180px' : '0')};
  max-height: ${({ $height }) => $height}px;
  width: max-content;
  overflow: hidden;
  flex-shrink: 0;
  transition: max-width 0.2s ease;
`

type ChartLegendProps = React.ComponentProps<typeof ChartLegend>

const LineLegendDrawer: React.FC<
  Pick<
    ChartLegendProps,
    'chartData' | 'selectedBreakdowns' | 'onDataPointHover'
  > & {
    open: boolean
    onToggle: () => void
    height: number
  }
> = ({
  chartData,
  selectedBreakdowns,
  onDataPointHover,
  open,
  onToggle,
  height,
}) => {
  const label = open ? 'Hide legend' : 'Show legend'
  return (
    <>
      <Tooltip content={label}>
        <LegendChevron
          type="button"
          aria-label={label}
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ? (
            <ChevronRight fontSize="inherit" />
          ) : (
            <ChevronLeft fontSize="inherit" />
          )}
        </LegendChevron>
      </Tooltip>
      <LineLegendBox $open={open} $height={height}>
        <ChartLegend
          chartData={chartData}
          selectedBreakdowns={selectedBreakdowns}
          onDataPointHover={onDataPointHover}
        />
      </LineLegendBox>
    </>
  )
}

const LegendChevron = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  align-self: stretch;
  padding: 0;
  margin: 0 2px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
  & svg {
    font-size: 18px;
  }
`

const ValidationError = styled.div`
  padding: 20px;
  color: #666;
`

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${askYogiTokens.spacing.md};
  margin-bottom: ${askYogiTokens.spacing.sm};
  min-height: 28px;
`

/* Title slot that flexes and wraps so long titles never crowd the buttons. */
const ChartTitleSlot = styled(AssetCaption)`
  flex: 1;
  min-width: 0;
`

const RightButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`

// A Sutra Tooltip around a DropdownMenu never shows (the menu drops its
// handlers), so a plain element sits between the two.
const DropdownTrigger = styled.span`
  display: inline-flex;
`

const ToolbarDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 18px;
  background: #e5e7eb;
  margin: 0 2px;
`

const MetricToggle = styled.div`
  display: flex;
  gap: 4px;
`

const MetricToggleButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: ${({ $active }) => ($active ? '#3b82f6' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#374151')};
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  &:hover {
    background: ${({ $active }) => ($active ? '#2563eb' : '#f3f4f6')};
  }
`
